import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import apiClient from '../../api/client';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';
import { FaSitemap, FaChevronDown, FaCheck } from 'react-icons/fa';

// ✅ Daftar komisi TIDAK lagi di-hardcode.
// Diambil otomatis dari backend lewat GET /komisi (endpoint index/list biasa),
// BUKAN endpoint /categories seperti TI Khusus — karena 1 komisi = 1 record
// hasil CRUD, jadi list ini otomatis reflect isi tabel `komisi` di database.
//
// Response dari getKomisiList() (Komisi::select('id','komisi','slug')->get()):
// [ { id, komisi: 'Crisis Center', slug: 'crisis_center' }, ... ]
// Catatan: apiClient.getKomisi(slug) yang sudah ada juga mengembalikan objek
// dengan field "komisi" (bukan "title") sebagai nama, sesuai kolom di DB.

const Komisi = () => {
    // --- Daftar komisi dari backend ---
    const [komisiList, setKomisiList] = useState([]);
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);
    const [categoriesError, setCategoriesError] = useState(null);

    // --- Tab aktif sekarang berupa slug (string), bukan object hardcode ---
    const [activeSlug, setActiveSlug] = useState(null);

    const [komisiData, setKomisiData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Tutup dropdown saat klik di luar area dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 1. Ambil daftar komisi dari BE (sekali saat mount)
    useEffect(() => {
        let isCancelled = false;
        const fetchCategories = async () => {
            try {
                // ⚠️ Tambahkan method ini di apiClient kamu (mirror dari getKomisi,
                // asumsinya apiClient sudah unwrap ke isi "data" dari responseOnSuccess):
                //   getKomisiList: () => http.get('/komisi').then(res => res.data.data)
                const response = await apiClient.getKomisiList();
                const list = Array.isArray(response) ? response : (response?.data ?? []);

                if (!isCancelled) {
                    setKomisiList(list);
                    // Set tab aktif ke item pertama dari BE (default)
                    if (list.length > 0) {
                        setActiveSlug(list[0].slug);
                    }
                }
            } catch (err) {
                console.error('Error fetching komisi categories:', err);
                if (!isCancelled) {
                    setKomisiList([]);
                    setCategoriesError('Gagal memuat daftar komisi.');
                }
            } finally {
                if (!isCancelled) setCategoriesLoaded(true);
            }
        };
        fetchCategories();
        return () => { isCancelled = true; };
    }, []);

    // 2. Ambil isi/body komisi setiap kali activeSlug berubah
    useEffect(() => {
        if (!activeSlug) return;

        let isCancelled = false;
        const fetchKomisiData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await apiClient.getKomisi(activeSlug);
                if (!isCancelled) setKomisiData(response);
            } catch (err) {
                console.error('Error fetching komisi data:', err);
                if (!isCancelled) setError('Gagal memuat data komisi. Silakan coba lagi nanti.');
            } finally {
                if (!isCancelled) setIsLoading(false);
            }
        };

        fetchKomisiData();
        return () => { isCancelled = true; };
    }, [activeSlug]);

    // ✅ Bersihkan HTML dari editor (Quill/CKEditor/dll) supaya tampilan konsisten
    const getCleanBodyHtml = (rawHtml) => {
        if (!rawHtml) return '';
        return DOMPurify.sanitize(rawHtml, {
            FORBID_ATTR: ['style', 'class'],
            FORBID_TAGS: ['script'],
        });
    };

    const activeKomisi = komisiList.find((k) => k.slug === activeSlug);
    const activeName = activeKomisi?.komisi || '';

    return (
        <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">

                {/* 🌟 Header Halaman */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 relative overflow-hidden border-b-8 border-purple-600">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-50/40 to-white transform skew-x-3 -translate-x-1/2 opacity-70"></div>
                    <div className="relative z-10 text-center">
                        <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-2">
                            KOMISI-KOMISI
                        </h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto font-medium">
                            Susunan Komisi Pelayanan Jemaat GPIB Kasih Karunia Badung Bali.
                        </p>
                    </div>
                </div>

                {/* 🗂️ Navigasi Dropdown (dari backend) */}
                <div className="mt-8 bg-white rounded-2xl shadow-xl p-4 sm:p-6 relative">
                    <h2 className="text-center font-bold text-gray-700 mb-4 border-b pb-2">Pilih Komisi</h2>

                    <div className="max-w-sm mx-auto relative" ref={dropdownRef}>
                        {/* Tombol Trigger Dropdown */}
                        <button
                            type="button"
                            onClick={() => setIsDropdownOpen((prev) => !prev)}
                            disabled={!categoriesLoaded || komisiList.length === 0}
                            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white border-2 shadow-sm transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed
                                ${isDropdownOpen ? 'border-gray-400 ring-2 ring-gray-200' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                            <span className="flex items-center gap-2 font-bold text-gray-800 text-left">
                                {!categoriesLoaded
                                    ? 'Memuat daftar komisi...'
                                    : (activeName || 'Pilih Komisi')}
                            </span>
                            <FaChevronDown
                                className={`text-gray-400 text-sm shrink-0 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                            />
                        </button>

                        {/* Daftar Pilihan Dropdown */}
                        {isDropdownOpen && komisiList.length > 0 && (
                            <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto">
                                {komisiList.map((tab) => {
                                    const isSelected = activeSlug === tab.slug;
                                    return (
                                        <button
                                            key={tab.slug}
                                            type="button"
                                            onClick={() => {
                                                setActiveSlug(tab.slug);
                                                setIsDropdownOpen(false);
                                            }}
                                            className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left text-sm font-semibold transition-colors duration-100
                                                ${isSelected ? 'bg-gray-100 text-gray-900' : 'text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            <span>{tab.komisi}</span>
                                            {isSelected && <FaCheck className="text-gray-400 text-xs shrink-0" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {categoriesError && (
                            <p className="text-center text-xs text-red-500 font-semibold mt-2">{categoriesError}</p>
                        )}
                    </div>
                </div>

                {/* 📋 Area Konten Utama */}
                <div className="mt-8 bg-white rounded-2xl shadow-xl p-6 sm:p-10 min-h-[400px] relative">
                    <div className="mb-6 border-b pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                            <h2 className="text-2xl font-black text-purple-600">
                                {activeName}
                            </h2>
                            {komisiData && komisiData.title && (
                                <p className="text-sm text-gray-500 mt-1">{komisiData.title}</p>
                            )}
                        </div>
                        <div className="hidden sm:flex p-3 rounded-full bg-purple-50 text-purple-600 text-2xl">
                            <FaSitemap />
                        </div>
                    </div>

                    {!categoriesLoaded || isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <p className="text-red-500 font-semibold">{error}</p>
                        </div>
                    ) : komisiList.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                            <p className="text-lg font-medium mb-1">Belum Ada Komisi Terdaftar</p>
                            <p className="text-sm">Daftar komisi belum tersedia dari server.</p>
                        </div>
                    ) : (
                        <PageTransitionWrapper>
                            {komisiData && komisiData.body ? (
                                <div
                                    className="komisi-body overflow-x-auto"
                                    dangerouslySetInnerHTML={{ __html: getCleanBodyHtml(komisiData.body) }}
                                />
                            ) : (
                                <div className="text-center py-16 text-gray-400">
                                    <p className="text-lg font-medium mb-1">Susunan Komisi Belum Tersedia</p>
                                    <p className="text-sm">Data untuk komisi ini sedang dalam proses pembaharuan.</p>
                                </div>
                            )}
                        </PageTransitionWrapper>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Komisi;